import { Component, ComponentFactoryResolver, ComponentRef, EventEmitter, Injector, Input, Output, SimpleChanges, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerSearchResultModel } from '@max/customer/search/models/customer-search-result.model';

@Component({
    selector: 'search-results',
    templateUrl: './search-results.html'
})
export class SearchResultsComponent {
    @Input() results: CustomerSearchResultModel;
    @Input() searchString: string;
    @Input() context: number;
    @Input() readOnly: boolean;
    @Output() onItemSelected = new EventEmitter<any>();
    @Output() onItemRemoved = new EventEmitter<any>();

    private componentRef: ComponentRef<any>;

    @ViewChild('searchResults', { read: ViewContainerRef, static: true }) searchResults: ViewContainerRef;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private componentFactoryResolver: ComponentFactoryResolver,
        private injector: Injector
    ) {}

    ngOnInit() {
        this.updateComponentWithSearchResults();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.updateChildInstanceProperty(changes, 'readOnly');
        this.updateChildInstanceProperty(changes, 'results');
    }

    ngOnDestroy() {
        this.clearComponent();
    }

    private clearComponent = (): void => {
        this.searchResults.clear();

        if (this.componentRef) {
            this.componentRef.destroy();
        }
    };

    private createComponentReference(componentType: Type<Component>) {
        const childComponent = this.componentFactoryResolver.resolveComponentFactory(componentType);
        const injector = Injector.create({
            providers: [{ provide: componentType, useValue: componentType, deps: [] }],
            parent: this.injector
        });
        return this.searchResults.createComponent(childComponent, 0, injector);
    }

    private defaultRouteData(): any {
        return this.router.config.find(c => c.path === '').data;
    }

    private getRouteData() {
        if (this.activatedRoute.snapshot.data && this.activatedRoute.snapshot.data['searchResults']) {
            return this.activatedRoute.snapshot.data;
        } else {
            return this.defaultRouteData();
        }
    }

    private replaceComponent = (componentType: Type<Component>): void => {
        this.clearComponent();

        if (componentType) {
            this.componentRef = this.createComponentReference(componentType);
            this.setupChildInstance(this.componentRef.instance);
        }
    };

    private setupChildInstance(childInstance: any) {
        childInstance.results = this.results;
        childInstance.readOnly = this.readOnly;
        childInstance.searchString = this.searchString;
        childInstance.onItemSelected.subscribe(r => this.onItemSelected.emit(r));
        if (childInstance.onItemRemoved) {
            childInstance.onItemRemoved.subscribe(r => this.onItemRemoved.emit(r));
        }
    }

    private updateChildInstanceProperty(changes: SimpleChanges, propertyName: string) {
        if (changes[propertyName] && changes[propertyName].currentValue !== undefined) {
            if (this.componentRef && this.componentRef.instance && this.componentRef.instance[propertyName] !== undefined) {
                this.componentRef.instance[propertyName] = changes[propertyName].currentValue;
            }
        }
    }

    private updateComponentWithSearchResults() {
        const resultsArray = this.getRouteData()['searchResults'];
        const searchResult = resultsArray && resultsArray.find(c => c.context === +this.context);
        const componentType: Type<Component> = searchResult && searchResult.component;
        this.replaceComponent(componentType);
    }
}
